import React, { useEffect, useRef, useState } from 'react';
import { SxProps, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useAuth } from 'services/auth/hooks';
import { ToastType, useToast } from 'services/toast';
import { AuthLayout } from 'services/auth/components/AuthLayout';
import { Paths } from 'services/navigation/paths';
import { Form, Fields, emailSchema } from 'common/form';
import { RouterLink } from 'common/ui/RouterLink';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isSafeInternalPath } from 'services/auth/utils/ssoRedirect';
import { takePendingSso } from 'services/auth/utils/ssoReauth';
import { Loading } from 'components/Loading';

const forgotPasswordStyle: SxProps = {
  color: 'primary.main',
  fontWeight: 500,
  fontSize: '14px',
  alignSelf: 'flex-end',
  cursor: 'pointer'
};

const validationSchema = Yup.object({
  email: emailSchema,
  password: Yup.string().required('Password is required')
});
type FormData = Yup.InferType<typeof validationSchema>;

const fields: Fields<FormData> = {
  email: {
    type: 'text',
    label: 'Email'
  },
  password: { type: 'password', label: 'Password' }
};

export const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, federatedSignIn, startSsoLogin } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');
  const ssoParam = searchParams.get('sso');
  const nextParam = searchParams.get('next');
  const ssoTriggered = useRef(false);
  // The deep-link redirects straight back out, so showing the form in the
  // meantime only flashes a login screen the user cannot use.
  const [isSsoRedirecting, setIsSsoRedirecting] = useState(Boolean(ssoParam));

  // Auto-start SSO when deep-linked with ?sso=<provider>.
  // The value is passed straight through as the Cognito identity_provider;
  // Cognito only accepts providers configured on the pool, so an arbitrary
  // value can't redirect anywhere. If it fails, we fall back to the login form.
  // ?next=<relative-path> is carried through the OAuth state and applied after
  // sign-in (see AuthRedirectListener); only relative internal paths are used.
  // The deep-link signs the current user out first, then lands back here
  // without ?sso= to resume against the provider it stashed.
  useEffect(() => {
    if (ssoTriggered.current) return;

    if (ssoParam) {
      ssoTriggered.current = true;
      const next = isSafeInternalPath(nextParam) ? nextParam : undefined;
      startSsoLogin({ provider: ssoParam, next }).catch(() => {
        setIsSsoRedirecting(false);
        navigate(Paths.LOGIN, { replace: true });
      });
      return;
    }

    const pending = takePendingSso();
    if (!pending) return;
    ssoTriggered.current = true;
    setIsSsoRedirecting(true);
    federatedSignIn({
      provider: pending.provider,
      customState: pending.next
    }).catch(() => {
      setIsSsoRedirecting(false);
      navigate(Paths.LOGIN, { replace: true });
    });
  }, [ssoParam, nextParam, startSsoLogin, federatedSignIn, navigate]);

  const handleFormSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      await signIn(data.email, data.password);
      setIsLoading(false);
    } catch (error) {
      showToast({
        message: 'We could not sign you in',
        type: ToastType.Error
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSsoRedirecting) return <Loading />;

  return (
    <>
      <AuthLayout title="Log in to Popp" isSubmitting={isLoading}>
        <Form
          schema={validationSchema}
          fields={fields}
          onSubmit={handleFormSubmit}
          defaultValues={{
            email: emailParam || '',
            password: ''
          }}
          submitButtonProps={{
            label: isLoading ? 'Loading...' : 'Continue',
            disabled: isLoading,
            fullWidth: true,
            sx: {
              py: '16px'
            }
          }}
        >
          <Typography sx={forgotPasswordStyle}>
            <RouterLink to={Paths.RESET_PASSWORD}>Forgot password?</RouterLink>
          </Typography>
        </Form>
      </AuthLayout>
    </>
  );
};
